import { parseSearchQuery } from "./searchParser"

export interface SchemaLike {
  id: number
  name: string
}

export interface SearchWhereResult {
  where: Record<string, unknown>
  // When `schema:X` (or `is:X` falling back to schema) names something
  // the schemas store doesn't know about, the consumer can use this to
  // distinguish "no records match" from "no schema named X".
  unresolvedSchema?: string
}

export interface BuildSearchWhereDeps {
  schemas: SchemaLike[]
  isValidType: (name: string) => boolean
}

export const buildSearchWhere = (
  query: string,
  { schemas, isValidType }: BuildSearchWhereDeps
): SearchWhereResult => {
  const trimmed = query.trim()
  if (trimmed.length < 2) return { where: { id: { _eq: -1 } } }

  const filters = parseSearchQuery(trimmed)
  const conditions: Record<string, unknown>[] = []
  let unresolvedSchema: string | undefined

  const resolveSchemaIds = (name: string) =>
    schemas.filter((s) => s.name.toLowerCase() === name.toLowerCase()).map((s) => s.id)

  if (filters.schema) {
    const ids = resolveSchemaIds(filters.schema)
    if (ids.length) {
      conditions.push({ schema_id: { _in: ids } })
    } else {
      conditions.push({ id: { _eq: -1 } })
      unresolvedSchema = filters.schema
    }
  }

  if (filters.type) {
    if (isValidType(filters.type)) {
      conditions.push({ type: { _ilike: `${filters.type}%` } })
    } else {
      const ids = resolveSchemaIds(filters.type)
      if (ids.length) {
        conditions.push({ schema_id: { _in: ids } })
      } else {
        conditions.push({ type: { _ilike: `${filters.type}%` } })
        unresolvedSchema = filters.type
      }
    }
  }

  if (filters.name) {
    conditions.push({
      _or: [
        { name: { _ilike: `%${filters.name}%` } },
        { description: { _ilike: `%${filters.name}%` } },
      ],
    })
  } else if (!filters.isStructured) {
    conditions.push({
      _or: [{ name: { _ilike: `%${trimmed}%` } }, { description: { _ilike: `%${trimmed}%` } }],
    })
  }

  if (filters.tag) {
    conditions.push({
      taggings: { tag: { name: { _ilike: `%${filters.tag}%` } } },
    })
  }

  // Always constrain on archived state, even when no other filter matched —
  // an empty `conditions` degrades to `{}` (unconstrained match-all), which
  // is how a bare `is:archived` used to return arbitrary records.
  conditions.push({ archived_at: { _is_null: !filters.archivedOnly } })

  const where = conditions.length > 0 ? { _and: conditions } : {}
  return { where, unresolvedSchema }
}
